import React from 'react';
import {useDispatch, useSelector} from "react-redux";
import Cookies from 'js-cookie';
import {AppDispatch} from "../../../utils/store";
import {Modals, showModal} from "../../../utils/modalSlice";
import {selectIsLoggedIn} from "../../../utils/userSlice";
import {useSiteContentValue} from "../../../context/SiteContentContext";
import HeroDemoPanel from "./HeroDemoPanel";
import HeroStatBadges from "./HeroStatBadges";
import FeatureGrid from "./FeatureGrid";
import PreviewRibbon from "./PreviewRibbon";
import regoLogo from "../../../assets/images/rego-logo.png";
import './hero.css';

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// scrollIntoView + the target's CSS scroll-margin-top, rather than hardcoded
// header math: the AppBar is 92px only on desktop and auto-height on mobile.
const scrollToSection = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (!element) {
        return;
    }

    element.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
    });
};

// Every line after the first carries the accent color, per the design.
const renderTitle = (title: string) => {
    const lines = title.split('\n');

    return lines.map((line, index) => (
        <React.Fragment key={`${index}-${line}`}>
            {index > 0 && <br/>}
            {index === 0 ? line : <span className="lnk-hero__title-accent">{line}</span>}
        </React.Fragment>
    ));
};

// Plain-text field: newlines become line breaks and the tail after the em dash
// on the last line is emphasised (design §3). No em dash -> plain text.
const renderSubtitle = (subtitle: string) => {
    const lines = subtitle.split('\n');

    return lines.map((line, index) => {
        const isLast = index === lines.length - 1;
        const dashIndex = isLast ? line.indexOf('—') : -1;

        return (
            <React.Fragment key={`${index}-${line}`}>
                {index > 0 && <br/>}
                {dashIndex === -1 ? line : (
                    <>
                        {line.slice(0, dashIndex + 1)}
                        <strong className="lnk-hero__subtitle-strong">{line.slice(dashIndex + 1)}</strong>
                    </>
                )}
            </React.Fragment>
        );
    });
};

const HomeHero: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const {content, isPreview} = useSiteContentValue();

    const handlePrimaryCta = () => {
        // isLoggedIn is `undefined` until App's checkLoginStatus effect runs; the
        // cookie is the same synchronous source that effect reads, so an already
        // logged-in user clicking immediately never gets the register modal.
        const loggedIn = isLoggedIn ?? !!Cookies.get('accessToken');

        if (loggedIn) {
            scrollToSection('package-list');
        } else {
            dispatch(showModal(Modals.REGISTER));
        }
    };

    const handleSecondaryCta = () => scrollToSection('site-list');

    return (
        <section className="lnk-hero" dir="rtl">
            <div className="lnk-hero__grid">
                <div className="lnk-hero__copy">
                    <span className="lnk-hero__eyebrow">
                        <span>מבית</span>
                        <a href="https://rego.co.il/" target="_blank" rel="noopener noreferrer"
                           className="lnk-hero__eyebrow-link" aria-label="ReGo">
                            <img src={regoLogo} alt="ReGo" width={25} height={16}/>
                        </a>
                    </span>

                    <h1 className="lnk-hero__title">{renderTitle(content.hero.title)}</h1>

                    <p className="lnk-hero__subtitle">{renderSubtitle(content.hero.subtitle)}</p>

                    <div className="lnk-hero__ctas">
                        <button type="button" className="lnk-hero__cta lnk-hero__cta--primary"
                                onClick={handlePrimaryCta}>
                            {content.hero.ctaPrimaryLabel}
                        </button>
                        <button type="button" className="lnk-hero__cta lnk-hero__cta--secondary"
                                onClick={handleSecondaryCta}>
                            {content.hero.ctaSecondaryLabel}
                        </button>
                    </div>
                </div>

                {/* The card is decorative fiction; the badges beside it are the real
                    content, which is why they are siblings and not children. */}
                <div className="lnk-hero__demo">
                    <div className="lnk-hero__demo-frame" aria-hidden="true">
                        <HeroDemoPanel/>
                    </div>
                    <HeroStatBadges stats={content.stats}/>
                </div>
            </div>

            <FeatureGrid/>

            {isPreview && <PreviewRibbon/>}
        </section>
    );
};

export default HomeHero;
